/**
 * Loops for the first kit's props (props.ts, props2.ts, props3.ts): banners and flags in the wind, hanging lanterns
 * swaying, flames and lit paper flickering, bamboo swaying. Rects are in each prop's still sprite (see kit/propAnim.ts);
 * re-check them if a maker's layout changes (scripts: docs/anim/props/).
 */
import { registerPropAnim, type AnimSpec } from './propAnim';

const banner: AnimSpec = {
  frames: 6,
  period: 2.4,
  pad: [0, 0, 2, 0],
  ops: [{ op: 'bend', rect: [11, 17, 28, 74], axis: 'x', pin: 'top', amp: 1.6, pow: 1.2, wave: 28, stretch: true }],
};
for (const c of ['Red', 'Blue', 'Jade', 'Gold', 'White', 'Black']) registerPropAnim(`banner${c}`, banner);

const bannerT: AnimSpec = {
  frames: 6,
  period: 2.6,
  pad: [0, 0, 2, 0],
  ops: [
    { op: 'bend', rect: [12, 12, 27, 70], axis: 'x', pin: 'top', amp: 1.4, pow: 1.2, wave: 26, stretch: true },
    { op: 'bend', rect: [27, 14, 32, 25], axis: 'x', pin: 'top', amp: 1 },
  ],
};
registerPropAnim('bannerTRed', bannerT);
registerPropAnim('bannerTBlue', bannerT);

const pennant: AnimSpec = {
  frames: 6,
  period: 1.8,
  pad: [0, 1, 0, 1],
  ops: [{ op: 'bend', rect: [10, 5, 44, 28], axis: 'y', pin: 'left', amp: 1.5, wave: 16 }],
};
registerPropAnim('pennantRed', pennant);
registerPropAnim('pennantJade', pennant);

registerPropAnim('flagString', {
  frames: 6,
  period: 2.2,
  pad: [0, 0, 0, 1],
  ops: [{ op: 'bend', rect: [23, 4, 62, 32], axis: 'y', pin: 'mid', amp: 1.2, wave: 30 }],
});
registerPropAnim('lanternString', {
  frames: 6,
  period: 2.8,
  pad: [0, 0, 0, 1],
  ops: [{ op: 'bend', rect: [27, 4, 66, 38], axis: 'y', pin: 'mid', amp: 1.2 }],
});
registerPropAnim('dryingLine', {
  frames: 6,
  period: 2.6,
  ops: [{ op: 'bend', rect: [27, 8, 55, 37], axis: 'x', pin: 'top', amp: 1.2, wave: 30 }],
});

// Hanging lanterns sway from their cords.
registerPropAnim('lanternPost', {
  frames: 6,
  period: 2.8,
  pad: [0, 0, 1, 0],
  ops: [{ op: 'bend', rect: [16, 23, 27, 38], axis: 'x', pin: 'top', amp: 1.2 }],
});
registerPropAnim('lanternPole2', {
  frames: 6,
  period: 3,
  pad: [1, 0, 1, 0],
  ops: [
    { op: 'bend', rect: [7, 10, 18, 26], axis: 'x', pin: 'top', amp: 1.2 },
    { op: 'bend', rect: [26, 23, 38, 36], axis: 'x', pin: 'top', amp: 1.2, wave: -40 },
  ],
});

// Flames and lit paper flicker within their own colours.
const flame = (rect: readonly [number, number, number, number]): AnimSpec => ({
  frames: 4,
  period: 0.6,
  ops: [{ op: 'flicker', rect, key: 'warm', band: 2 }],
});
registerPropAnim('brazier', flame([4, 0, 21, 13]));
registerPropAnim('campfire', flame([13, 11, 28, 28]));
registerPropAnim('torchPost', flame([6, 9, 17, 22]));
const glow = (rect: readonly [number, number, number, number]): AnimSpec => ({
  frames: 4,
  period: 1.2,
  ops: [{ op: 'flicker', rect, key: 'bright', band: 4 }],
});
registerPropAnim('palaceLantern', glow([7, 8, 25, 29]));
// Stone and pagoda lanterns hold one paper colour: their light pool flickers (lighting.ts), the stone stays still.

// Bamboo sways from its roots.
const bamboo: AnimSpec = {
  frames: 6,
  period: 3.4,
  pad: [1, 0, 1, 0],
  ops: [{ op: 'bend', rect: [0, 0, 60, 96], axis: 'x', pin: 'bottom', amp: 1.4, pow: 2 }],
};
registerPropAnim('bambooClump', bamboo);
registerPropAnim('bambooClump2', bamboo);
